import React from 'react';
import { useSelector } from 'react-redux';
import { DARK_THEME_CSS, LAYOUT_CONFIG } from '../../../constants';
import {
  safeExecute,
  validateHeightConstraint,
  withErrorBoundary,
} from '../../../js/text/validationUtils';
import { useTextEditorContext } from '../TextEditorContext';
import { EnhancedTextModal } from '../components/EnhancedTextModal';
import { SliderControlsPanel } from '../components/SliderControlsPanel';
import TabletFloatingToolbar from '../components/TabletFloatingToolbar';
import { useResponsive } from '../hooks/useResponsive';
import { useTextEditor } from '../hooks/useTextEditor';

function TextEditorComponent() {
  // State for managing expanded panels
  const [activePanel, setActivePanel] = React.useState(null);
  const {
    // State
    text,
    setText,
    color,
    fontFamily,
    scale,
    rotate,
    textList,
    setTextList,
    activeTextId,
    showFloatingInput,
    setShowFloatingInput,
    setShouldMaintainFocus,
    inputRef,
    isSubmitting, // Add submission guard state
    // Functions
    focusInput,
    preventScaleReset,
    findTextByIdMemo,
    handleSubmit,
    handleUpdateText,
    handleFontChange,
    handleColorChange,
    handleScaleChange,
    handleRotateChange,
    handleDeleteText,
    handleReset,
    handleNewText,
    deselectText,
  } = useTextEditor();
  const { showText } = useSelector((state) => state.control);

  // Get context for sharing state
  const { updateTextEditorState } = useTextEditorContext();

  // Update context when state changes
  React.useEffect(() => {
    updateTextEditorState({ activeTextId, text, showFloatingInput });
  }, [activeTextId, text, showFloatingInput]);

  // Auto focus color panel when text is selected or new text is added
  React.useEffect(() => {
    if (activeTextId) {
      const foundText = findTextByIdMemo(activeTextId);
      if (foundText) {
        // Text is selected, auto focus color panel
        setActivePanel('color');
      } else {
        setActivePanel(null);
      }
    } else if (showFloatingInput) {
      // New text is being added, auto focus color panel
      setActivePanel('color');
    } else {
      // No active text, close all panels
      setActivePanel(null);
    }
  }, [activeTextId, showFloatingInput]);

  // Set global functions for TextActions to access
  React.useEffect(() => {
    window.handleNewText = handleNewText;
    window.handleReset = handleReset;
    window.handleDeleteText = handleDeleteText;
    window.deselectText = deselectText;

    // Cleanup on unmount
    return () => {
      delete window.handleNewText;
      delete window.handleReset;
      delete window.handleDeleteText;
      delete window.deselectText;
    };
  }, [handleNewText, handleReset, handleDeleteText, deselectText]);

  // Responsive design hook
  const { isSmallHeight, isNarrowWidth } = useResponsive();

  // Validate height constraint
  React.useEffect(() => {
    const isValid = validateHeightConstraint(LAYOUT_CONFIG.TOTAL_HEIGHT, 130);
    if (!isValid) {
      console.error('TextEditor height constraint violation detected');
    }
  }, []);

  return (
    <div
      onClick={(e) => {
        // Prevent focus loss when clicking on configuration controls
        // But don't interfere with font button functionality
        if (
          e.target.closest('.MuiSlider-root') ||
          e.target.closest('.color-swatch') ||
          e.target.closest('.clickable')
        ) {
          setShouldMaintainFocus(true);
        }
        // Don't handle font-button clicks here to avoid conflicts
      }}
      className="text-editor-container"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <style>
        {`
          /* Dark theme styles */
          ${DARK_THEME_CSS}
          
          .color-preview:hover .color-tooltip {
            opacity: 1 !important;
          }
          
          /* Ensure proper cursor behavior */
          button, [role="button"], .clickable {
            cursor: pointer !important;
          }
          
          /* Slider cursor improvements */
          .MuiSlider-root {
            cursor: pointer !important;
          }
          
          .MuiSlider-thumb {
            cursor: grab !important;
          }
          
          .MuiSlider-thumb:active {
            cursor: grabbing !important;
          }
          
          /* Color swatch cursor improvements */
          .color-swatch {
            cursor: pointer !important;
          }
          
          /* Font button cursor improvements */
          .font-button {
            cursor: pointer !important;
            position: relative !important;
            z-index: 10 !important;
            pointer-events: auto !important;
          }
          
          /* Ensure font buttons are not blocked */
          .font-button * {
            pointer-events: auto !important;
          }
          
          /* Ensure sliders don't overflow their containers */
          .MuiSlider-root {
            max-width: 100% !important;
            overflow: visible !important;
          }
          
          /* Tablet-specific responsive design styles */
          @media (min-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT}px) and (max-width: 1024px) {
            /* Tablet-specific styles */
            /* Enhanced touch targets for tablet */
            button, [role="button"], .clickable {
              min-height: ${LAYOUT_CONFIG.MIN_TOUCH_TARGET}px;
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
            }
            
            /* Prevent zoom on input focus */
            input, textarea {
              font-size: 16px !important;
            }
          }
          
          /* Large tablet optimizations */
          @media (min-width: 1025px) and (max-width: 1366px) {
            .text-editor-container {
              padding: 0;
            }
            
            .floating-input-tablet {
              max-width: 90vw !important;
              min-width: 400px !important;
            }
          }
          
          /* Small height optimizations */
          @media (max-height: 600px) {
            .floating-input-tablet {
              top: 10% !important;
            }
            
            .text-elements-list {
              max-height: 200px;
              overflow-y: auto;
            }
          }
          
          /* Tablet text list positioning */
          @media (min-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT}px) {
            .text-elements-list {
              top: 5px !important;
              right: 5px !important;
              width: calc(100vw - 20px) !important;
              max-width: 400px !important;
            }
          }
        `}
      </style>

      {/* Tablet Floating Toolbar - always render so Add New stays visible; actions enabled only when text is active and modal closed */}
      <TabletFloatingToolbar
        hasActiveText={showText && !showFloatingInput}
        activePanel={activePanel}
        setActivePanel={setActivePanel}
        handleColorChange={handleColorChange}
        handleFontChange={handleFontChange}
        handleDeleteText={handleDeleteText}
        focusInput={focusInput}
        handleNewText={handleNewText}
        color={color}
        fontFamily={fontFamily}
        activeTextId={activeTextId}
        findTextByIdMemo={findTextByIdMemo}
        setText={setText}
        setShouldMaintainFocus={setShouldMaintainFocus}
        setShowFloatingInput={setShowFloatingInput}
        deselectText={deselectText}
      />

      {/* Floating Slider Controls Panel - show only when a text exists in model and is focused (no modal) */}
      {showText && !showFloatingInput && (
        <SliderControlsPanel
          scale={scale}
          rotate={rotate}
          onScaleChange={handleScaleChange}
          onRotateChange={handleRotateChange}
          preventScaleReset={preventScaleReset}
          setShouldMaintainFocus={setShouldMaintainFocus}
        />
      )}

      {/* Enhanced Floating Text Input Modal */}
      <EnhancedTextModal
        text={text}
        setText={setText}
        showFloatingInput={showFloatingInput}
        setShowFloatingInput={setShowFloatingInput}
        activeTextId={activeTextId}
        isSubmitting={isSubmitting}
        textList={textList}
        setTextList={setTextList}
        findTextByIdMemo={findTextByIdMemo}
        handleSubmit={handleSubmit}
        handleUpdateText={handleUpdateText}
        focusInput={focusInput}
        inputRef={inputRef}
        setShouldMaintainFocus={setShouldMaintainFocus}
        isSmallHeight={isSmallHeight}
        isNarrowWidth={isNarrowWidth}
      />
    </div>
  );
}

// Export TextEditor with error boundary
export const TextEditorTablet = withErrorBoundary(
  TextEditorComponent,
  <div
    style={{
      height: '130px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 20, 30, 0.95)',
      border: '1px solid #FF4444',
      borderRadius: '12px',
      color: '#FF4444',
      fontSize: '14px',
      fontWeight: '500',
    }}
  >
    TextEditor Error: Please refresh the page
  </div>,
);
